import { FormEvent, useState } from 'react';
import Eyebrow from '../Eyebrow';
import Wordmark from '../Wordmark';
import { ContactLine, reviewInput, reviewPrimaryGlowButton, reviewSecondaryButton } from './ReviewUi';

interface ReviewEntryPanelProps {
  state: 'loading' | 'pin' | 'identity' | 'error';
  message?: string;
  busy: boolean;
  onPin: (pin: string) => Promise<void>;
  onIdentify: (name: string, email: string | null) => Promise<void>;
  onRetry: () => Promise<void>;
}

export default function ReviewEntryPanel({ state, message, busy, onPin, onIdentify, onRetry }: ReviewEntryPanelProps) {
  const [pin, setPin] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  async function submitPin(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onPin(pin);
  }

  async function submitIdentity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    await onIdentify(name, email.trim() || null);
  }

  if (state === 'loading') {
    return <main className="min-h-screen bg-page" aria-hidden="true" />;
  }

  return (
    <main className="min-h-screen bg-page px-5 py-6 text-fg md:px-10 md:py-9">
      <header><Wordmark /></header>
      <section className="mx-auto grid min-h-[75vh] max-w-2xl place-items-center py-12">
        <div className="w-full border-t border-line pt-8">
          <Eyebrow accent className="rise">Client review</Eyebrow>
          {state === 'pin' ? (
            <>
              <h1 className="title-xl rise mt-4" style={{ animationDelay: '60ms' }}>Enter your review PIN</h1>
              <p className="rise mt-4 max-w-xl text-muted" style={{ animationDelay: '120ms' }}>Use the PIN from the email that brought you here.</p>
              <form className="rise-subtle mt-8 max-w-md" style={{ animationDelay: '200ms' }} onSubmit={submitPin}>
                <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">
                  Review PIN
                  <input className={reviewInput} type="password" inputMode="numeric" autoComplete="one-time-code" value={pin} onChange={(event) => setPin(event.target.value)} autoFocus required aria-describedby={message ? 'pin-error' : undefined} />
                </label>
                {message ? <p id="pin-error" className="mt-3 rounded-input bg-error-tint px-4 py-3 text-sm text-error" role="alert">{message}</p> : null}
                <button type="submit" className={`${reviewPrimaryGlowButton} mt-5`} disabled={busy || !pin.trim()}>{busy ? 'Checking PIN' : 'Open review'}</button>
              </form>
              <ContactLine />
            </>
          ) : state === 'identity' ? (
            <>
              <h1 className="title-xl rise mt-4" style={{ animationDelay: '60ms' }}>Tell us who is reviewing</h1>
              <p className="rise mt-4 max-w-xl text-muted" style={{ animationDelay: '120ms' }}>Add your name so Henry knows whose picks these are. Your email is optional.</p>
              <form className="rise-subtle mt-8 max-w-md" style={{ animationDelay: '200ms' }} onSubmit={submitIdentity}>
                <label className="font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">
                  Your name
                  <input className={reviewInput} value={name} onChange={(event) => setName(event.target.value)} maxLength={80} autoComplete="name" autoFocus required aria-describedby={message ? 'identity-error' : undefined} />
                </label>
                <label className="mt-5 block font-mono text-[11px] font-semibold uppercase tracking-[0.14em] text-faint">
                  Email, optional
                  <input className={reviewInput} type="email" value={email} onChange={(event) => setEmail(event.target.value)} maxLength={254} autoComplete="email" />
                </label>
                <p className="mt-4 text-xs text-faint">We only use these details to attribute your review.</p>
                {message ? <p id="identity-error" className="mt-4 rounded-input bg-error-tint px-4 py-3 text-sm text-error" role="alert">{message}</p> : null}
                <button type="submit" className={`${reviewPrimaryGlowButton} mt-5`} disabled={busy || !name.trim()}>{busy ? 'Saving your name' : 'Continue to review'}</button>
              </form>
              <ContactLine />
            </>
          ) : (
            <>
              <h1 className="title-xl rise mt-4" style={{ animationDelay: '60ms' }}>We could not open this review</h1>
              <p className="mt-4 max-w-xl text-muted" role="alert">{message ?? 'Something got in the way. Mind trying again?'}</p>
              <button type="button" className={`${reviewSecondaryButton} mt-7`} onClick={() => void onRetry()} disabled={busy}>{busy ? 'Trying again' : 'Try again'}</button>
              <ContactLine />
            </>
          )}
        </div>
      </section>
    </main>
  );
}
